import React, { useEffect, useState } from "react";
import { api, formatMoney, CURRENCIES } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, EnvelopeSimple, ArrowClockwise, FileArrowUp, FileArrowDown, Paperclip } from "@phosphor-icons/react";
const today = () => new Date().toISOString().slice(0, 10);
const empty = {
  property_id: "", room: "", full_name: "", phone: "", email: "", id_number: "",
  lease_start: "", lease_end: "", deposit: 0, deposit_currency: "TRY", deposit_rate_to_try: 1,
  deposit_date: today(),
  monthly_rent: 0, rent_currency: "TRY", payment_day: 1,
  bank_name: "", bank_iban: "", bank_account_holder: "",
  notes: "", active: true,
};

const emptyRenew = { lease_start: "", lease_end: "", monthly_rent: 0, rent_currency: "TRY", notes: "" };

export default function Tenants() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewForm, setRenewForm] = useState(emptyRenew);
  const [renewingId, setRenewingId] = useState(null);

  const load = async () => {
    const [ts, ps] = await Promise.all([api.get("/tenants"), api.get("/properties")]);
    setItems(ts.data); setProps(ps.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) { toast.error(t("select_property")); return; }
    const payload = {
      ...form,
      deposit: parseFloat(form.deposit) || 0,
      deposit_rate_to_try: parseFloat(form.deposit_rate_to_try) || 1,
      monthly_rent: parseFloat(form.monthly_rent) || 0,
      payment_day: parseInt(form.payment_day) || 1,
    };
    try {
      if (editingId) await api.put(`/tenants/${editingId}`, payload);
      else await api.post("/tenants", payload);
      toast.success("OK");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/tenants/${id}`);
    load();
  };

  const edit = (x) => {
    setForm({ ...empty, ...x });
    setEditingId(x.id);
    setOpen(true);
  };

  const openRenew = (x) => {
    setRenewingId(x.id);
    setRenewForm({
      lease_start: x.lease_end || today(),
      lease_end: "",
      monthly_rent: x.monthly_rent || 0,
      rent_currency: x.rent_currency || "TRY",
      notes: "",
    });
    setRenewOpen(true);
  };

  const submitRenew = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tenants/${renewingId}/renew`, {
        ...renewForm,
        monthly_rent: parseFloat(renewForm.monthly_rent) || 0,
      });
      toast.success("OK");
      setRenewOpen(false); setRenewingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const sendReminder = async (tenant) => {
    try {
      await api.post("/notifications/send", { kind: "due_today", tenant_id: tenant.id });
      toast.success(t("email_sent"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [docsOpen, setDocsOpen] = useState(false);
  const [docsTenant, setDocsTenant] = useState(null);
  const [uploadKind, setUploadKind] = useState("contract");

  const openDocs = (tenant) => {
    setDocsTenant(tenant);
    setDocsOpen(true);
  };

  const uploadDoc = async (file) => {
    if (!file || !docsTenant) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tenants/${docsTenant.id}/documents?kind=${uploadKind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("OK");
      const { data } = await api.get("/tenants");
      setItems(data);
      setDocsTenant(data.find((x) => x.id === docsTenant.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadDoc = async (doc) => {
    const res = await fetch(`${api.defaults.baseURL}/tenants/${docsTenant.id}/documents/${doc.id}`, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/tenants/${docsTenant.id}/documents/${doc.id}`);
    const { data } = await api.get("/tenants");
    setItems(data);
    setDocsTenant(data.find((x) => x.id === docsTenant.id));
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "-";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{t("tenants")}</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900">{t("tenants")}</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-tenant-btn" className="bg-slate-900 hover:bg-slate-800 rounded-md">
              <Plus size={16} className="mr-2" /> {t("add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? t("edit") : t("add")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label-eyebrow">{t("property")}</label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="tenant-property"><SelectValue placeholder={t("select_property")} /></SelectTrigger>
                  <SelectContent>
                    {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="label-eyebrow">{t("room")}</label>
                <Input data-testid="tenant-room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("full_name")}</label>
                <Input data-testid="tenant-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("phone")}</label>
                <Input data-testid="tenant-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("email")}</label>
                <Input data-testid="tenant-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("id_number")}</label>
                <Input data-testid="tenant-id-number" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("payment_day")}</label>
                <Input data-testid="tenant-day" type="number" min={1} max={31} value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("lease_start")}</label>
                <Input data-testid="tenant-start" type="date" required value={form.lease_start} onChange={(e) => setForm({ ...form, lease_start: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("lease_end")}</label>
                <Input data-testid="tenant-end" type="date" required value={form.lease_end} onChange={(e) => setForm({ ...form, lease_end: e.target.value })} /></div>

              <div className="col-span-2 pt-2 border-t border-slate-200"><div className="label-eyebrow text-slate-500">Kira</div></div>
              <div><label className="label-eyebrow">{t("monthly_rent")}</label>
                <Input data-testid="tenant-rent" type="number" step="0.01" required value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("rent_currency")}</label>
                <Select value={form.rent_currency} onValueChange={(v) => setForm({ ...form, rent_currency: v })}>
                  <SelectTrigger data-testid="tenant-rent-cur"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="col-span-2 pt-2 border-t border-slate-200"><div className="label-eyebrow text-slate-500">Depozito</div></div>
              <div><label className="label-eyebrow">{t("deposit")}</label>
                <Input data-testid="tenant-deposit" type="number" step="0.01" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("deposit_currency")}</label>
                <Select value={form.deposit_currency} onValueChange={(v) => setForm({ ...form, deposit_currency: v })}>
                  <SelectTrigger data-testid="tenant-dep-cur"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="label-eyebrow">{t("deposit_date")}</label>
                <Input data-testid="tenant-dep-date" type="date" value={form.deposit_date} onChange={(e) => setForm({ ...form, deposit_date: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("deposit_rate")}</label>
                <div className="flex gap-2">
                  <Input data-testid="tenant-dep-rate" type="number" step="0.0001" value={form.deposit_rate_to_try} onChange={(e) => setForm({ ...form, deposit_rate_to_try: e.target.value })} />
                  {form.deposit_currency !== "TRY" && (
                    <button type="button" data-testid="fetch-rate-dep" onClick={async () => {
                      try {
                        const { data } = await api.get("/fx/rates");
                        const r = data[form.deposit_currency];
                        if (r) { setForm({ ...form, deposit_rate_to_try: r }); toast.success(`${t("fetch_rate_ok")}: ${r}`); }
                        else toast.error(t("fetch_rate_err"));
                      } catch { toast.error(t("fetch_rate_err")); }
                    }} className="text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-900 border border-slate-200 px-2 py-0.5 rounded whitespace-nowrap">
                      {t("fetch_rate")}
                    </button>
                  )}
                </div>
              </div>

              <div className="col-span-2 pt-2 border-t border-slate-200"><div className="label-eyebrow text-slate-500">Banka</div></div>
              <div><label className="label-eyebrow">{t("bank_name")}</label>
                <Input data-testid="tenant-bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("bank_holder")}</label>
                <Input data-testid="tenant-holder" value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} /></div>
              <div className="col-span-2"><label className="label-eyebrow">{t("bank_iban")}</label>
                <Input data-testid="tenant-iban" value={form.bank_iban} onChange={(e) => setForm({ ...form, bank_iban: e.target.value })} /></div>

              <div className="col-span-2"><label className="label-eyebrow">{t("notes")}</label>
                <Textarea data-testid="tenant-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter className="col-span-2">
                <Button data-testid="tenant-save" type="submit" className="bg-slate-900 hover:bg-slate-800">{t("save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("renew")}</DialogTitle></DialogHeader>
          <form onSubmit={submitRenew} className="grid grid-cols-2 gap-3">
            <div><label className="label-eyebrow">{t("new_lease_start")}</label>
              <Input data-testid="renew-start" type="date" required value={renewForm.lease_start} onChange={(e) => setRenewForm({ ...renewForm, lease_start: e.target.value })} /></div>
            <div><label className="label-eyebrow">{t("new_lease_end")}</label>
              <Input data-testid="renew-end" type="date" required value={renewForm.lease_end} onChange={(e) => setRenewForm({ ...renewForm, lease_end: e.target.value })} /></div>
            <div><label className="label-eyebrow">{t("new_rent")}</label>
              <Input data-testid="renew-rent" type="number" step="0.01" required value={renewForm.monthly_rent} onChange={(e) => setRenewForm({ ...renewForm, monthly_rent: e.target.value })} /></div>
            <div><label className="label-eyebrow">{t("rent_currency")}</label>
              <Select value={renewForm.rent_currency} onValueChange={(v) => setRenewForm({ ...renewForm, rent_currency: v })}>
                <SelectTrigger data-testid="renew-cur"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><label className="label-eyebrow">{t("notes")}</label>
              <Textarea data-testid="renew-notes" value={renewForm.notes} onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })} /></div>
            <DialogFooter className="col-span-2">
              <Button data-testid="renew-save" type="submit" className="bg-slate-900 hover:bg-slate-800">{t("save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={docsOpen} onOpenChange={setDocsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("documents")} — {docsTenant?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-end gap-3 border-b border-slate-200 pb-4">
              <div className="flex-1">
                <label className="label-eyebrow">Tür</label>
                <Select value={uploadKind} onValueChange={setUploadKind}>
                  <SelectTrigger data-testid="doc-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">{t("kind_contract")}</SelectItem>
                    <SelectItem value="id">{t("kind_id")}</SelectItem>
                    <SelectItem value="other">{t("kind_other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 h-10 bg-slate-900 text-white rounded-md hover:bg-slate-800 text-sm">
                <FileArrowUp size={16} /> {t("upload")}
                <input data-testid="doc-upload-input" type="file" className="hidden" onChange={(e) => uploadDoc(e.target.files?.[0])} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" />
              </label>
            </div>
            <div className="space-y-2">
              {(docsTenant?.documents || []).length === 0 && (
                <div className="text-center text-slate-500 text-sm py-6">{t("no_documents")}</div>
              )}
              {(docsTenant?.documents || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between border border-slate-200 rounded-md p-3" data-testid={`doc-${d.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{d.filename}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="uppercase tracking-widest font-bold">{t(`kind_${d.kind}`)}</span>
                      <span className="mx-2 text-slate-300">•</span>
                      {(d.size / 1024).toFixed(1)} KB
                      <span className="mx-2 text-slate-300">•</span>
                      {d.uploaded_at?.slice(0, 10)}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => downloadDoc(d)} className="p-2 hover:bg-slate-100 rounded" data-testid={`doc-dl-${d.id}`}>
                      <FileArrowDown size={16} />
                    </button>
                    <button onClick={() => deleteDoc(d)} className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`doc-del-${d.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 label-eyebrow">{t("full_name")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("property")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("phone")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("lease_end")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("bank_name")}</th>
              <th className="text-right px-4 py-3 label-eyebrow">{t("monthly_rent")}</th>
              <th className="text-right px-4 py-3 label-eyebrow">{t("payment_day")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">{t("no_data")}</td></tr>}
            {items.map((x) => (
              <tr key={x.id} data-testid={`tenant-row-${x.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{x.full_name}</td>
                <td className="px-4 py-3 text-slate-600">{propName(x.property_id)} {x.room && <span className="text-xs text-slate-400">/ {x.room}</span>}</td>
                <td className="px-4 py-3 text-slate-600">{x.phone || "-"}</td>
                <td className="px-4 py-3 text-slate-600">{x.lease_end}</td>
                <td className="px-4 py-3 text-slate-600">{x.bank_name || "-"}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatMoney(x.monthly_rent, x.rent_currency)}</td>
                <td className="px-4 py-3 text-right text-slate-600">{x.payment_day}</td>
                <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => openRenew(x)} className="p-2 hover:bg-slate-100 rounded" data-testid={`renew-${x.id}`} title={t("renew")}>
                    <ArrowClockwise size={16} />
                  </button>
                  <button onClick={() => sendReminder(x)} className="p-2 hover:bg-slate-100 rounded" data-testid={`remind-${x.id}`} title={t("send_reminder")}>
                    <EnvelopeSimple size={16} />
                  </button>
                  <button onClick={() => edit(x)} className="p-2 hover:bg-slate-100 rounded" data-testid={`tedit-${x.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => del(x.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`tdel-${x.id}`}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
